import { useMemo, useOptimistic } from 'react';
import { toast } from 'sonner';

import { useMy } from '@/hooks';
import { useUpdateSetting } from '@/hooks/useSetting';

export type MonitoringCountMode = 'rolling-30d' | 'last-scan';

const SETTING_NAME = 'monitoring-count-mode';

export function useMonitoringCountMode(email?: string) {
  const { data: settings } = useMy({ resource: 'setting' }, { email });
  const { mutateAsync: updateSetting } = useUpdateSetting(email);

  const serverMode = useMemo<MonitoringCountMode>(() => {
    const setting = settings?.find(s => s.name === SETTING_NAME);
    return setting?.value === 'rolling-30d' ? 'rolling-30d' : 'last-scan';
  }, [settings]);

  const [optimisticMode, setOptimisticMode] = useOptimistic(
    serverMode,
    (_current, newMode: MonitoringCountMode) => newMode
  );

  const handleToggle = async (mode: MonitoringCountMode) => {
    try {
      setOptimisticMode(mode);
      await updateSetting({
        name: SETTING_NAME,
        value: mode,
        silent: true,
      });
      toast.success('Monitoring count mode updated');
    } catch (error) {
      toast.error('Failed to update monitoring count mode');
      console.error('Failed to update monitoring count mode:', error);
    }
  };

  return { mode: optimisticMode, handleToggle };
}
